import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Plus, Calendar, AlertTriangle, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import SharedAbwesenheitenTabs from '../components/SharedAbwesenheitenTabs';

const api = (path, opts) => fetch(path, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const MONAT_NAMEN_DE = ['', 'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONAT_NAMEN_EN = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WOCHENTAG_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WOCHENTAG_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// TYP_OPTIONS removed — now loaded dynamically from /api/abwesenheitsarten

// #1848: Abteilungsauswahl überlebt Seitenwechsel (wie Karten-Zustände #1841)
const ABT_FILTER_LS_KEY = 'weship_abwkal_abteilungen_v1';

// #1848: Zeitraum TT.MM.–TT.MM.JJJJ, rein stringbasiert (kein new Date() = kein UTC-Shift)
function fmtZeitraum(vonIso, bisIso) {
  const v = parseDate(vonIso);
  const b = parseDate(bisIso || vonIso);
  if (!v) return '';
  const [vy, vm, vd] = v.split('-');
  const [by, bm, bd] = (b || v).split('-');
  if (v === b || !b) return `${vd}.${vm}.${vy}`;
  return vy === by ? `${vd}.${vm}.–${bd}.${bm}.${by}` : `${vd}.${vm}.${vy}–${bd}.${bm}.${by}`;
}

function dateFmt(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDate(s) {
  if (!s) return null;
  // If already a YYYY-MM-DD string, return as-is (avoid new Date() UTC timezone shift)
  if (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Abteilungs-Mehrfachauswahl (#1848) ───────────────────────
function AbteilungMultiSelect({ abteilungen, selected, onChange, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (name) => {
    onChange(selected.includes(name) ? selected.filter(a => a !== name) : [...selected, name]);
  };

  const label = selected.length === 0
    ? (t('urlaub.kalender.alleAbteilungen') || 'Alle Abteilungen')
    : selected.length === 1
      ? selected[0]
      : t('urlaub.kalender.nAbteilungen', '{n} Abteilungen', { n: selected.length });

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border-default)] rounded-lg bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]"
      >
        <span className={selected.length ? 'font-semibold text-[var(--text-primary)]' : ''}>{label}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-40 mt-1 left-0 min-w-[210px] bg-[var(--bg-card,#fff)] border border-[var(--border-default)] rounded-lg shadow-sm py-1 max-h-72 overflow-y-auto">
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]"
          >
            {t('urlaub.kalender.alleAbteilungen') || 'Alle Abteilungen'}
          </button>
          <div className="border-t border-[var(--border-default)] my-1" />
          {abteilungen.map(a => (
            <label key={a} className="flex items-center gap-2 px-3 py-1.5 text-xs text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selected.includes(a)}
                onChange={() => toggle(a)}
                className="w-3.5 h-3.5 rounded border-[var(--border-default)]"
              />
              {a}
            </label>
          ))}
          {abteilungen.length === 0 && (
            <div className="px-3 py-1.5 text-xs text-[var(--text-muted)]">{t('common.noData') || 'Keine Daten'}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Antrags-Modal ─────────────────────────────────────────────
function AntragModal({ open, onClose, onSuccess, maId, startDate, mitarbeiter, t, lang, abwesenheitsarten = [] }) {
  const [form, setForm] = useState({ mitarbeiter_id: '', von_datum: '', bis_datum: '', typ: 'urlaub', notiz_ma: '', stundenweise: false, von_zeit: '', bis_zeit: '' });
  const [konto, setKonto] = useState(null);
  const [berechneteTage, setBerechneteTage] = useState(null);
  const [validierung, setValidierung] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      mitarbeiter_id: maId || '',
      von_datum: startDate || '',
      bis_datum: startDate || '',
      typ: 'urlaub',
      notiz_ma: '',
      stundenweise: false,
      von_zeit: '',
      bis_zeit: '',
    });
    setKonto(null);
    setBerechneteTage(null);
    setValidierung(null);
  }, [open, maId, startDate]);

  // Konto laden wenn MA gewählt (use aktuell = live-calculated saldo)
  useEffect(() => {
    if (!form.mitarbeiter_id) { setKonto(null); return; }
    api(`/api/urlaub/konto/${form.mitarbeiter_id}`).then(d => {
      setKonto(d.aktuell || null);
    }).catch(() => setKonto(null));
  }, [form.mitarbeiter_id]);

  // Tage berechnen bei Datumsänderung (serverseitig inkl. Feiertage)
  useEffect(() => {
    if (!form.von_datum || !form.bis_datum || !form.mitarbeiter_id) {
      setBerechneteTage(null);
      setValidierung(null);
      return;
    }
    if (form.von_datum > form.bis_datum) { setBerechneteTage(0); return; }
    api(`/api/urlaub/berechne-arbeitstage?von=${form.von_datum}&bis=${form.bis_datum}&mitarbeiter_id=${form.mitarbeiter_id}`)
      .then(r => setBerechneteTage(r.tage ?? null))
      .catch(() => setBerechneteTage(null));
  }, [form.von_datum, form.bis_datum, form.mitarbeiter_id]);

  // #1701: Gleitzeittag nur für Gleitzeit-MA anbieten (Backend-Guard gespiegelt)
  const selectedMa = mitarbeiter.find(m => String(m.id) === String(form.mitarbeiter_id));
  const waehlbareArten = abwesenheitsarten.filter(a => a.key !== 'gleitzeittag' || selectedMa?.gleitzeit_aktiv);
  useEffect(() => {
    if (form.typ === 'gleitzeittag' && !selectedMa?.gleitzeit_aktiv) setForm(f => ({ ...f, typ: 'urlaub' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.mitarbeiter_id]);

  const handleSubmit = async () => {
    if (!form.mitarbeiter_id || !form.von_datum || !form.bis_datum) return;
    setSubmitting(true);
    try {
      const selectedArt = abwesenheitsarten.find(a => a.key === form.typ);
      const quelle = selectedArt?.quelle || 'urlaub_antraege';
      let res;
      if (quelle === 'urlaub_antraege') {
        res = await apiPost('/api/urlaub/antraege', {
          mitarbeiter_id: parseInt(form.mitarbeiter_id),
          von_datum: form.von_datum, bis_datum: form.bis_datum,
          typ: form.typ, notiz_ma: form.notiz_ma || undefined,
        });
      } else if (quelle === 'krankenstaende') {
        res = await apiPost('/api/urlaub/krankenstaende', {
          mitarbeiter_id: parseInt(form.mitarbeiter_id),
          von_datum: form.von_datum, bis_datum: form.bis_datum,
          typ: form.typ, notiz: form.notiz_ma || undefined,
        });
      } else {
        if (form.stundenweise && (!form.von_zeit || !form.bis_zeit)) {
          setValidierung({ fehler: [{ text: t('abwesenheiten.zeitenPflicht', 'Bitte Von- und Bis-Zeit angeben') }], warnungen: [] });
          setSubmitting(false);
          return;
        }
        res = await apiPost('/api/abwesenheiten', {
          mitarbeiter_id: parseInt(form.mitarbeiter_id),
          art_key: form.typ, von_datum: form.von_datum,
          bis_datum: form.bis_datum,
          von_zeit: form.stundenweise ? form.von_zeit : undefined,
          bis_zeit: form.stundenweise ? form.bis_zeit : undefined,
          grund: form.notiz_ma || undefined,
        });
      }
      if (res.success) {
        setValidierung(null);
        onSuccess();
        onClose();
      }
    } catch (err) {
      setValidierung({ fehler: err.fehler || [{ text: err.error || err.message }], warnungen: err.warnungen || [] });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const nachAntrag = konto && berechneteTage != null
    ? (parseFloat(konto.saldo || 0) - berechneteTage)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('urlaub.antrag.title')}</h3>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
        {/* MA */}
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('nav.employees')}</label>
          <select
            className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
            value={form.mitarbeiter_id}
            onChange={e => setForm(f => ({ ...f, mitarbeiter_id: e.target.value }))}
          >
            <option value="">—</option>
            {mitarbeiter.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>

        {/* Typ */}
        <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.typ')}</label>
        <select
          className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
          value={form.typ}
          onChange={e => setForm(f => ({ ...f, typ: e.target.value }))}
        >
          {abwesenheitsarten.length > 0 ? (
            <>
              {abwesenheitsarten.filter(a => a.quelle === 'urlaub_antraege').length > 0 && (
                <optgroup label={t('urlaub.gruppe.urlaub', 'Urlaub')}>
                  {abwesenheitsarten.filter(a => a.quelle === 'urlaub_antraege').map(a => (
                    <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>
                  ))}
                </optgroup>
              )}
              {abwesenheitsarten.filter(a => a.quelle === 'krankenstaende').length > 0 && (
                <optgroup label={t('urlaub.gruppe.krankenstand', 'Krankenstand')}>
                  {abwesenheitsarten.filter(a => a.quelle === 'krankenstaende').map(a => (
                    <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>
                  ))}
                </optgroup>
              )}
              {waehlbareArten.filter(a => a.quelle === 'abwesenheiten').length > 0 && (
                <optgroup label={t('urlaub.gruppe.abwesenheit', 'Sonstige Abwesenheit')}>
                  {waehlbareArten.filter(a => a.quelle === 'abwesenheiten').map(a => (
                    <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>
                  ))}
                </optgroup>
              )}
            </>
          ) : (
            <>
              <option value="urlaub">{t('urlaub.typ.urlaub', 'Urlaub')}</option>
              <option value="zeitausgleich">{t('urlaub.typ.zeitausgleich', 'Zeitausgleich')}</option>
            </>
          )}
        </select>
        </div>

        {/* Datum */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.von')}</label>
            <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={form.von_datum} onChange={e => setForm(f => ({ ...f, von_datum: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.bis')}</label>
            <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={form.bis_datum} onChange={e => setForm(f => ({ ...f, bis_datum: e.target.value }))} />
          </div>
        </div>

        {/* Arbeitstage */}
        {berechneteTage != null && (
          <div className="text-sm text-[var(--text-secondary)]">
            {t('urlaub.antrag.tage')}: <span className="font-semibold text-[var(--text-primary)]">{berechneteTage}</span> {t('urlaub.tage')}
          </div>
        )}

        {/* #1005 Stundenweise-Toggle: nur wenn Art erlaubt_stundenweise=true */}
        {(() => {
          const sa = abwesenheitsarten.find(a => a.key === form.typ);
          if (!sa?.erlaubt_stundenweise || sa.quelle !== 'abwesenheiten') return null;
          return (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
                <input type="checkbox" className="rounded border-[var(--border-default)]"
                  checked={form.stundenweise} onChange={e => setForm(f => ({ ...f, stundenweise: e.target.checked }))} />
                {t('abwesenheiten.stundenweiseErfassen', 'Stundenweise erfassen')}
              </label>
              {form.stundenweise && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheiten.vonZeit', 'Von Zeit')}</label>
                    <input type="time" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                      value={form.von_zeit} onChange={e => setForm(f => ({ ...f, von_zeit: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheiten.bisZeit', 'Bis Zeit')}</label>
                    <input type="time" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                      value={form.bis_zeit} onChange={e => setForm(f => ({ ...f, bis_zeit: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Kontingent-Info */}
        {konto && form.typ === 'urlaub' && (
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">{t('urlaub.anspruch')}</span>
              <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.anspruch || 0)} {t('urlaub.tage')}</span>
            </div>
            {konto.in_wartefrist && (
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">{t('urlaub.aufgebaut')}</span>
                <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.aufgebaut || 0).toFixed(2)} {t('urlaub.tage')}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">− {t('urlaub.verbraucht')}</span>
              <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.verbraucht || 0)} {t('urlaub.tage')}</span>
            </div>
            {parseFloat(konto.geplant || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">− {t('urlaub.geplant')}</span>
                <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.geplant || 0)} {t('urlaub.tage')}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-[var(--border-default)] pt-1 mt-1">
              <span className="font-semibold text-[var(--text-primary)]">{t('urlaub.verfuegbareTage')}</span>
              <span className="font-bold text-[var(--text-primary)]">{Math.round(parseFloat(konto.saldo || 0) * 100) / 100}</span>
            </div>
            {nachAntrag != null && (
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">{t('urlaub.konto.nachAntrag')}</span>
                <span className={`font-semibold ${nachAntrag < 0 ? 'text-red-600' : 'text-[var(--text-primary)]'}`}>
                  {nachAntrag.toFixed(1)} {t('urlaub.tage')} {nachAntrag >= 0 ? '' : ''}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Warnungen / Fehler */}
        {validierung?.fehler?.length > 0 && (
          <div className="space-y-1">
            {validierung.fehler.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{f.text}
              </div>
            ))}
          </div>
        )}
        {validierung?.warnungen?.length > 0 && (
          <div className="space-y-1">
            {validierung.warnungen.map((w, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{w.text}
              </div>
            ))}
          </div>
        )}

        {/* Kommentar */}
        <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.kommentar')}</label>
        <textarea
          className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
          rows={2} value={form.notiz_ma} onChange={e => setForm(f => ({ ...f, notiz_ma: e.target.value }))}
        />
        </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.cancel') || 'Abbrechen'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.mitarbeiter_id || !form.von_datum || !form.bis_datum}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white font-semibold disabled:opacity-50 transition-colors"
          >
            {submitting ? '...' : t('urlaub.antrag.einreichen')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Krankenstand-Erfassungs-Modal ────────────────────────────
function KrankenstandModal({ open, onClose, onSuccess, maId, mitarbeiter, t }) {
  const [form, setForm] = useState({ mitarbeiter_id: '', von_datum: '', bis_datum: '', typ: 'krankenstand', bestaetigung: false, notiz: '' });
  const [overlap, setOverlap] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({ mitarbeiter_id: maId || '', von_datum: '', bis_datum: '', typ: 'krankenstand', bestaetigung: false, notiz: '' });
    setOverlap(null);
    setError(null);
  }, [open, maId]);

  // Check for vacation overlap when dates change
  useEffect(() => {
    if (!form.mitarbeiter_id || !form.von_datum) { setOverlap(null); return; }
    api(`/api/urlaub/antraege?ma_id=${form.mitarbeiter_id}&status=genehmigt`).then(d => {
      const antraege = d.data || [];
      const von = new Date(form.von_datum + 'T00:00:00');
      const bis = form.bis_datum ? new Date(form.bis_datum + 'T00:00:00') : von;
      const found = antraege.find(a => {
        const aVon = new Date(a.von_datum + 'T00:00:00');
        const aBis = new Date(a.bis_datum + 'T00:00:00');
        return von <= aBis && bis >= aVon;
      });
      if (found) {
        // Calculate overlap days
        const oStart = von > new Date(found.von_datum + 'T00:00:00') ? von : new Date(found.von_datum + 'T00:00:00');
        const oEnd = bis < new Date(found.bis_datum + 'T00:00:00') ? bis : new Date(found.bis_datum + 'T00:00:00');
        const kalTage = Math.ceil((oEnd - oStart) / (1000 * 60 * 60 * 24)) + 1;
        setOverlap({ antrag: found, tage: kalTage });
      } else {
        setOverlap(null);
      }
    }).catch(() => setOverlap(null));
  }, [form.mitarbeiter_id, form.von_datum, form.bis_datum]);

  const berechneteTage = (() => {
    if (!form.von_datum || !form.bis_datum) return null;
    const von = new Date(form.von_datum + 'T00:00:00');
    const bis = new Date(form.bis_datum + 'T00:00:00');
    const kalTage = Math.ceil((bis - von) / (1000 * 60 * 60 * 24)) + 1;
    return kalTage;
  })();

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    // §5 UrlaubsG: If overlap with approved vacation AND >= 3 days, use special endpoint
    if (overlap && berechneteTage >= 3) {
      try {
        const res = await apiPost(`/api/urlaub/krankenstand-im-urlaub/${overlap.antrag.id}`, {
          von_datum: form.von_datum, bis_datum: form.bis_datum, notiz: form.notiz,
        });
        if (res.success) {
          // Also update bestaetigung if set
          if (form.bestaetigung && res.data?.krankenstand?.id) {
            await api(`/api/urlaub/krankenstaende/${res.data.krankenstand.id}`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ bestaetigung: true }),
            });
          }
          onSuccess?.();
          onClose();
        } else {
          setError(res.error || 'Fehler');
        }
      } catch (err) {
        setError(err.error || err.message || 'Fehler');
      }
    } else {
      // Normal sick leave entry
      try {
        const res = await apiPost('/api/urlaub/krankenstaende', {
          mitarbeiter_id: parseInt(form.mitarbeiter_id),
          von_datum: form.von_datum,
          bis_datum: form.bis_datum || null,
          typ: form.typ,
          notiz: form.notiz || null,
        });
        if (res.success) {
          if (form.bestaetigung && res.data?.id) {
            await api(`/api/urlaub/krankenstaende/${res.data.id}`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ bestaetigung: true }),
            });
          }
          onSuccess?.();
          onClose();
        } else {
          setError(res.error || 'Fehler');
        }
      } catch (err) {
        setError(err.error || err.message || 'Fehler');
      }
    }
    setSubmitting(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('krankenstand.erfassen')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
        {/* MA Select */}
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('nav.employees')}</label>
          <select
            className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
            value={form.mitarbeiter_id} onChange={e => setForm(f => ({ ...f, mitarbeiter_id: e.target.value }))}
          >
            <option value="">—</option>
            {mitarbeiter.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>

        {/* Typ */}
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('krankenstand.typ')}</label>
          <select
            className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
            value={form.typ} onChange={e => setForm(f => ({ ...f, typ: e.target.value }))}
          >
            <option value="krankenstand">{t('krankenstand.typ.krankenstand')}</option>
            <option value="arbeitsunfall">{t('krankenstand.typ.arbeitsunfall')}</option>
            <option value="kur">{t('krankenstand.typ.kur')}</option>
            <option value="pflegefreistellung">{t('krankenstand.typ.pflege')}</option>
          </select>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('krankenstand.von')}</label>
            <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={form.von_datum} onChange={e => setForm(f => ({ ...f, von_datum: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('krankenstand.bis')}</label>
            <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={form.bis_datum} onChange={e => setForm(f => ({ ...f, bis_datum: e.target.value }))} />
          </div>
        </div>

        {berechneteTage && (
          <div className="text-xs text-[var(--text-tertiary)]">{t('krankenstand.tageBerechnet')}: <strong>{berechneteTage}</strong></div>
        )}

        {/* Bestätigung */}
        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
          <input type="checkbox" checked={form.bestaetigung} onChange={e => setForm(f => ({ ...f, bestaetigung: e.target.checked }))}
            className="rounded border-[var(--border-default)]" />
          {t('krankenstand.bestaetigung')}
        </label>

        {/* §5 UrlaubsG Warning */}
        {overlap && berechneteTage >= 3 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
            <div className="flex items-start gap-2 text-amber-800 font-medium">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              {t('krankenstand.imUrlaub')}
            </div>
            <p className="text-amber-700 mt-1">
              {t('krankenstand.urlaubGutschrift').replace('{{tage}}', overlap.tage)}
            </p>
            <p className="text-amber-600 mt-0.5">
              {t('urlaub.antrag.zeitraum')}: {fmtZeitraum(overlap.antrag.von_datum, overlap.antrag.bis_datum)}
            </p>
          </div>
        )}

        {/* Notiz */}
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('common.note') || 'Notiz'}</label>
          <textarea
            className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
            rows={2} value={form.notiz} onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))}
          />
        </div>

        {error && <div className="text-red-600 text-xs">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.cancel') || 'Abbrechen'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.mitarbeiter_id || !form.von_datum}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white font-semibold disabled:opacity-50 transition-colors"
          >
            {submitting ? '...' : t('krankenstand.erfassen')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail-Tooltip ────────────────────────────────────────────
function CellTooltip({ data, x, y, t, lang }) {
  if (!data) return null;
  // #1848: Art in Klartext — bevorzugt Labels aus `abwesenheitsarten` (SSoT, #1845),
  //   sonst i18n-Key; nie der rohe Schlüssel.
  const artLabel = (lang === 'de' ? data.label_de : (data.label_en || data.label_de))
    || (data.typ ? t(`urlaub.typ.${data.typ}`) : null)
    || data.art_key || data.typ;
  return (
    <div
      className="fixed z-50 bg-[var(--bg-card,#fff)] border border-[var(--border-default)] rounded-lg shadow-sm p-3 text-xs max-w-[220px]"
      style={{ left: Math.min(x, window.innerWidth - 240), top: y + 12 }}
    >
      <div className="font-semibold text-[var(--text-primary)] mb-1">{data.ma_name}</div>
      {artLabel && <div className="text-[var(--text-secondary)]">{artLabel}</div>}
      {data.status && <div className="text-[var(--text-secondary)]">{t(`urlaub.status.${data.status}`) || data.status}</div>}
      {data.von_datum && <div className="text-[var(--text-tertiary)]">{fmtZeitraum(data.von_datum, data.bis_datum)}</div>}
      {/* #1005 Stundenweise: zeige Zeit-Range statt Tage */}
      {data.von_zeit && data.bis_zeit
        ? <div className="text-[var(--text-tertiary)]">{String(data.von_zeit).slice(0, 5)}–{String(data.bis_zeit).slice(0, 5)} ({(data.stunden ?? 0).toString().replace('.', ',')} h)</div>
        : (data.tage && <div className="text-[var(--text-tertiary)]">{data.tage} {t('urlaub.tage')}</div>)}
      {data.notiz_ma && <div className="mt-1 text-[var(--text-muted)] italic">{data.notiz_ma}</div>}
      {data.name && <div className="text-[var(--text-secondary)]">{data.name}</div>}
    </div>
  );
}

// ─── Tagesansicht-Modal (#1227) ────────────────────────────────
// #1845: Kein Kategorie-Wörterbuch mehr. Beschriftung und Farbe kommen aus
//   `abwesenheitsarten` und werden vom Endpunkt mitgeliefert — ein zweiter
//   Satz Namen im Frontend hat aus jeder Pflegefreistellung „Urlaub" gemacht.
const ABW_FARBE_FALLBACK = '#6366F1';
function DayDetailModal({ open, datum, onClose, t, lang }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !datum) return;
    setLoading(true);
    setData(null);
    const abtParam = datum.abteilungen?.length ? `&abteilung=${encodeURIComponent(datum.abteilungen.join(','))}` : '';
    const formerParam = datum.includeFormer ? '&include_former=true' : '';
    api(`/api/urlaub/tag/${datum.dateStr}?${abtParam}${formerParam}`.replace('?&', '?'))
      .then(res => setData(res))
      .catch(() => setData({ eintraege: [], gesamt: 0, feiertag: null }))
      .finally(() => setLoading(false));
  }, [open, datum]);

  if (!open || !datum) return null;

  const dateObj = new Date(datum.dateStr + 'T00:00:00');
  const dateLabel = dateObj.toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
  const eintraege = data?.eintraege || [];

  const labelFor = (e) => (lang === 'de'
    ? (e.label_de || e.art_key)
    : (e.label_en || e.label_de || e.art_key));
  const colorFor = (e) => e.farbe || ABW_FARBE_FALLBACK;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border border-[var(--border-default)] shadow-lg w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-default)]">
          <div>
            <div className="text-sm font-semibold text-[var(--text-primary)]">{t('abwesenheiten.tagDetail.title')}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{dateLabel}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-muted)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          {data?.feiertag && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-[var(--bg-inset,#F8F9FC)] text-xs text-[var(--text-secondary)]">
              {t('urlaub.kalender.feiertag')}: {data.feiertag}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[var(--text-muted)]">
              <div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin mr-2" />
              {t('common.loading') || 'Laden...'}
            </div>
          ) : eintraege.length === 0 ? (
            <div className="text-center py-8 text-sm text-[var(--text-muted)]">{t('abwesenheiten.tagDetail.empty')}</div>
          ) : (
            <>
              <div className="text-xs text-[var(--text-tertiary)] mb-2">
                {t('abwesenheiten.tagDetail.count').replace('{n}', data?.gesamt ?? eintraege.length)}
              </div>
              <ul className="space-y-1.5">
                {eintraege.map(e => (
                  <li key={`${e.mitarbeiter_id}-${e.art_key}`} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-[var(--border-default)]">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorFor(e) }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[var(--text-primary)] truncate">{e.ma_name}</div>
                      {e.abteilung_name && <div className="text-[10px] text-[var(--text-muted)]">{e.abteilung_name}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-[var(--text-secondary)]">{labelFor(e)}</div>
                      {!e.ganztags && e.von_zeit && e.bis_zeit && (
                        <div className="text-[10px] text-[var(--text-tertiary)]">{String(e.von_zeit).slice(0, 5)}–{String(e.bis_zeit).slice(0, 5)}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function UrlaubKalenderPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const maFilter = searchParams.get('ma');
  const monatNamen = language === 'de' ? MONAT_NAMEN_DE : MONAT_NAMEN_EN;
  const wochentagNamen = language === 'de' ? WOCHENTAG_DE : WOCHENTAG_EN;

  const today = new Date();
  const [jahr, setJahr] = useState(today.getFullYear());
  const [monat, setMonat] = useState(today.getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [kalenderData, setKalenderData] = useState(null);
  const [konten, setKonten] = useState([]);
  // #1848: Mehrfachauswahl, persistiert je Browser (wie #1841-Karten)
  const [filterAbts, setFilterAbts] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem(ABT_FILTER_LS_KEY));
      return Array.isArray(v) ? v.filter(x => typeof x === 'string') : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(ABT_FILTER_LS_KEY, JSON.stringify(filterAbts)); } catch { /* voll/privat */ }
  }, [filterAbts]);
  const [scrollHeute, setScrollHeute] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyWithAbsence, setOnlyWithAbsence] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [showFormer, setShowFormer] = useState(false);
  const [antragModal, setAntragModal] = useState({ open: false, maId: maFilter ? parseInt(maFilter) : null, date: null });
  const [ksModal, setKsModal] = useState({ open: false, maId: null });
  const [abwesenheitsarten, setAbwesenheitsarten] = useState([]);
  const [dayDetail, setDayDetail] = useState({ open: false, datum: null });

  // ─── Load data ───
  const load = useCallback(() => {
    setLoading(true);
    const formerParam = showFormer ? '&include_former=true' : '';
    Promise.all([
      api(`/api/urlaub/kalender?jahr=${jahr}&monat=${monat}`),
      api(`/api/urlaub/konto?jahr=${jahr}${formerParam}`),
      api('/api/abwesenheitsarten'),
    ]).then(([kal, kon, arten]) => {
      setKalenderData(kal.data);
      setKonten(kon.data || []);
      setAbwesenheitsarten(arten.data || []);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [jahr, monat, showFormer]);

  useEffect(() => { load(); }, [load]);

  // ─── Calendar grid data ───
  const daysInMonth = new Date(jahr, monat, 0).getDate();
  const days = useMemo(() => {
    const arr = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(jahr, monat - 1, d);
      arr.push({
        day: d,
        weekday: date.getDay(),
        dateStr: dateFmt(date),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
      });
    }
    return arr;
  }, [jahr, monat, daysInMonth]);

  // Build lookup maps
  const feiertagSet = useMemo(() => {
    const set = new Set();
    kalenderData?.feiertage?.forEach(f => set.add(parseDate(f.datum)));
    return set;
  }, [kalenderData]);

  const sperrenSet = useMemo(() => {
    const set = new Set();
    kalenderData?.sperren?.forEach(s => {
      const von = new Date(s.von_datum + 'T00:00:00');
      const bis = new Date(s.bis_datum + 'T00:00:00');
      const cur = new Date(von);
      while (cur <= bis) {
        set.add(dateFmt(cur));
        cur.setDate(cur.getDate() + 1);
      }
    });
    return set;
  }, [kalenderData]);

  // MA → date → entry map
  const urlaubMap = useMemo(() => {
    const map = {};
    kalenderData?.urlaube?.forEach(u => {
      const von = new Date(u.von_datum + 'T00:00:00');
      const bis = new Date(u.bis_datum + 'T00:00:00');
      const cur = new Date(von);
      while (cur <= bis) {
        const ds = dateFmt(cur);
        if (!map[u.mitarbeiter_id]) map[u.mitarbeiter_id] = {};
        map[u.mitarbeiter_id][ds] = u;
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [kalenderData]);

  const krankMap = useMemo(() => {
    const map = {};
    kalenderData?.krankheiten?.forEach(k => {
      const von = new Date(k.von_datum + 'T00:00:00');
      const bis = k.bis_datum ? new Date(k.bis_datum + 'T00:00:00') : von;
      const cur = new Date(von);
      while (cur <= bis) {
        const ds = dateFmt(cur);
        if (!map[k.mitarbeiter_id]) map[k.mitarbeiter_id] = {};
        map[k.mitarbeiter_id][ds] = k;
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [kalenderData]);

  // Additional absences (Berufsschule, Karenz, etc.)
  const abwesenheitMap = useMemo(() => {
    const map = {};
    kalenderData?.abwesenheiten?.forEach(a => {
      const von = new Date(a.von_datum + 'T00:00:00');
      const bis = a.bis_datum ? new Date(a.bis_datum + 'T00:00:00') : von;
      const cur = new Date(von);
      while (cur <= bis) {
        const ds = dateFmt(cur);
        if (!map[a.mitarbeiter_id]) map[a.mitarbeiter_id] = {};
        map[a.mitarbeiter_id][ds] = a;
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [kalenderData]);

  // Unique Abteilungen — #1848: abteilungen.name (dieselbe Quelle wie der HR-Bereich)
  const abteilungen = useMemo(() => {
    const set = new Set();
    konten.forEach(k => { if (k.abteilung_name) set.add(k.abteilung_name); });
    return [...set].sort((a, b) => a.localeCompare(b, 'de'));
  }, [konten]);

  // Filtered MA list
  const mitarbeiterList = useMemo(() => {
    let list = konten;
    if (filterAbts.length) list = list.filter(k => filterAbts.includes(k.abteilung_name));
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(k => (k.ma_name || '').toLowerCase().includes(q));
    }
    if (onlyWithAbsence) {
      list = list.filter(k => {
        const id = k.mitarbeiter_id;
        return Boolean(urlaubMap[id] || krankMap[id] || abwesenheitMap[id]);
      });
    }
    return list;
  }, [konten, filterAbts, searchQuery, onlyWithAbsence, urlaubMap, krankMap, abwesenheitMap]);

  // Nav
  const goNext = () => { if (monat === 12) { setJahr(j => j + 1); setMonat(1); } else setMonat(m => m + 1); };
  const goPrev = () => { if (monat === 1) { setJahr(j => j - 1); setMonat(12); } else setMonat(m => m - 1); };
  // #1848: „Heute" springt zum Monat, markiert die Spalte (Scroll in Sicht) und
  //   öffnet den bestehenden Tagesdialog (#1227/#1845) — mit aktivem Abteilungsfilter.
  const goToday = () => {
    setJahr(today.getFullYear());
    setMonat(today.getMonth() + 1);
    setScrollHeute(true);
    setDayDetail({ open: true, datum: { dateStr: dateFmt(today), abteilungen: filterAbts, includeFormer: showFormer } });
  };
  useEffect(() => {
    if (!scrollHeute || loading) return;
    document.getElementById('kal-heute-spalte')?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    setScrollHeute(false);
  }, [scrollHeute, loading]);

  const isToday = (ds) => ds === dateFmt(today);
  const isAdmin = user?.rolle === 'administrator' || user?.rolle === 'ops_manager';

  function getCellInfo(maId, dateStr) {
    if (urlaubMap[maId]?.[dateStr]) return { type: urlaubMap[maId][dateStr].status === 'beantragt' ? 'beantragt' : 'genehmigt', data: urlaubMap[maId][dateStr] };
    if (krankMap[maId]?.[dateStr]) return { type: 'krankenstand', data: krankMap[maId][dateStr] };
    if (abwesenheitMap[maId]?.[dateStr]) {
      const a = abwesenheitMap[maId][dateStr];
      return { type: 'abwesenheit', data: a, label: a.kuerzel || a.art_key, color: a.farbe };
    }
    if (sperrenSet.has(dateStr)) return { type: 'gesperrt', data: kalenderData?.sperren?.[0] };
    return null;
  }

  const CELL_STYLES = {
    genehmigt:    'bg-green-100 border-green-300',
    beantragt:    'bg-yellow-100 border-yellow-300',
    krankenstand: 'bg-orange-100 border-orange-300',
    gesperrt:     'bg-red-50 border-red-200',
  };

  const CELL_DOT = {
    genehmigt: 'bg-green-500',
    beantragt: 'bg-yellow-500',
    krankenstand: 'bg-orange-500',
    gesperrt: 'bg-red-400',
  };

  // Helper: hex → inline cell style for dynamic absence colors
  function abwesenheitCellStyle(hex) {
    if (!hex) return {};
    return { backgroundColor: hex + '18', borderColor: hex, borderWidth: '1px', borderStyle: 'solid' };
  }

  return (
    <div className="p-6 max-w-full">
      <SharedAbwesenheitenTabs />
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-xl font-bold text-[var(--text-primary)]">{t('abwesenheiten.kalenderTitel')}</h1>
        <div className="flex items-center gap-2">
          {/* Month nav */}
          <button onClick={goPrev} className="p-2 rounded-lg border border-[var(--border-default)] hover:bg-[var(--bg-inset)]">
            <ChevronLeft size={16} />
          </button>
          <button onClick={goToday} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--border-default)] hover:bg-[var(--bg-inset)] text-[var(--text-secondary)]">
            {t('urlaub.kalender.heute') || 'Heute'}
          </button>
          <span className="text-base font-semibold text-[var(--text-primary)] min-w-[140px] text-center">
            {monatNamen[monat]} {jahr}
          </span>
          <button onClick={goNext} className="p-2 rounded-lg border border-[var(--border-default)] hover:bg-[var(--bg-inset)]">
            <ChevronRight size={16} />
          </button>

          {/* Filters */}
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('abwesenheiten.kalender.search_placeholder')}
            className="ml-4 px-3 py-1.5 text-xs border border-[var(--border-default)] rounded-lg bg-[var(--bg-card)] text-[var(--text-secondary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 focus:border-[#338edc]"
          />

          <AbteilungMultiSelect abteilungen={abteilungen} selected={filterAbts} onChange={setFilterAbts} t={t} />

          <label className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border-default)] rounded-lg bg-[var(--bg-card)] cursor-pointer select-none">
            <input type="checkbox" checked={showFormer} onChange={e => setShowFormer(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-[var(--border-default)]" />
            <span className="text-xs text-[var(--text-secondary)]">{t('mitarbeiter.zeigeEhemalige')}</span>
          </label>

          <label className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border-default)] rounded-lg bg-[var(--bg-card)] cursor-pointer select-none">
            <input type="checkbox" checked={onlyWithAbsence} onChange={e => setOnlyWithAbsence(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-[var(--border-default)]" />
            <span className="text-xs text-[var(--text-secondary)]">{t('abwesenheiten.kalender.only_with_absence')}</span>
          </label>

          {/* New request button + Krankenstand */}
          {isAdmin && (
            <>
              <button
                onClick={() => setKsModal({ open: true, maId: null })}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[10px] border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors"
              >
                <Plus size={14} /> {t('krankenstand.erfassen')}
              </button>
              <button
                onClick={() => setAntragModal({ open: true, maId: null, date: null })}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white transition-colors"
              >
                <Plus size={14} /> {t('urlaub.antrag.neu')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-[var(--text-secondary)]">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-100 border border-green-300" /> {t('urlaub.kalender.genehmigt')}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-yellow-100 border border-yellow-300" /> {t('urlaub.kalender.beantragt')}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-orange-100 border border-orange-300" /> {t('urlaub.kalender.krankenstand')}</span>
        {abwesenheitsarten.filter(a => a.key !== 'krankenstand' && a.key !== 'urlaub' && a.key !== 'zeitausgleich').map(art => (
          <span key={art.key} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm border" style={{ backgroundColor: (art.farbe || '#6366F1') + '25', borderColor: art.farbe || '#6366F1' }} />
            {language === 'de' ? art.label_de : (art.label_en || art.label_de)}
          </span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-50 border border-red-200" /> {t('urlaub.kalender.gesperrt')}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[var(--bg-inset)]" /> {t('urlaub.kalender.feiertag')}</span>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40 text-[var(--text-muted)]">
          <div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin mr-2" />
          {t('common.loading') || 'Laden...'}
        </div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[900px]">
              <thead>
                <tr style={{ background: 'var(--bg-inset,#F8F9FC)' }}>
                  <th className="sticky left-0 z-10 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider px-3 py-2 min-w-[160px]" style={{ background: 'var(--bg-inset,#F8F9FC)' }}>
                    {t('nav.employees')}
                  </th>
                  {days.map(d => {
                    const isFeiertag = feiertagSet.has(d.dateStr);
                    return (
                      <th key={d.day}
                        id={isToday(d.dateStr) ? 'kal-heute-spalte' : undefined}
                        className={`text-center text-[10px] font-medium px-0 py-1.5 min-w-[32px] cursor-pointer hover:bg-blue-50/60 ${
                          d.isWeekend || isFeiertag ? 'text-[var(--text-muted,#9CA3AF)]' : 'text-[var(--text-secondary)]'
                        } ${isToday(d.dateStr) ? 'bg-blue-50 border-b-2 border-[var(--primary,#338edc)]' : ''}`}
                        style={d.isWeekend || isFeiertag ? { background: 'var(--bg-inset,#F8F9FC)' } : {}}
                        title={t('abwesenheiten.tagDetail.openHint')}
                        onClick={() => setDayDetail({ open: true, datum: { dateStr: d.dateStr, abteilungen: filterAbts, includeFormer: showFormer } })}
                      >
                        <div>{wochentagNamen[d.weekday]}</div>
                        <div className={`text-xs font-semibold ${isToday(d.dateStr) ? 'text-[var(--primary,#338edc)]' : ''}`}>{d.day}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                {mitarbeiterList.map(ma => (
                  <tr key={ma.mitarbeiter_id} className="hover:bg-[var(--bg-inset,#F8F9FC)]/50">
                    <td className="sticky left-0 z-10 px-3 py-1.5 text-xs font-medium text-[var(--text-primary)] whitespace-nowrap bg-[var(--bg-card,#fff)]">
                      {ma.ma_name}
                      {ma.abteilung_name && <span className="ml-1.5 text-[10px] text-[var(--text-muted)]">{ma.abteilung_name}</span>}
                    </td>
                    {days.map(d => {
                      const isFeiertag = feiertagSet.has(d.dateStr);
                      const cell = getCellInfo(ma.mitarbeiter_id, d.dateStr);
                      const isWE = d.isWeekend;

                      return (
                        <td
                          key={d.day}
                          className={`text-center px-0 py-1 relative ${
                            isWE || isFeiertag ? 'bg-[var(--bg-inset,#F8F9FC)]' : ''
                          } ${isToday(d.dateStr) ? 'bg-blue-50/50' : ''} ${
                            cell && cell.type !== 'abwesenheit' ? CELL_STYLES[cell.type] || '' : ''
                          } ${!isWE && !isFeiertag && !cell ? 'cursor-pointer hover:bg-blue-50/30' : ''}`}
                          style={cell?.type === 'abwesenheit' ? abwesenheitCellStyle(cell.color) : undefined}
                          onClick={() => {
                            if (cell?.data) {
                              // Show tooltip on click for touch
                            } else if (!isWE && !isFeiertag && isAdmin) {
                              setAntragModal({ open: true, maId: ma.mitarbeiter_id, date: d.dateStr });
                            }
                          }}
                          onMouseEnter={e => {
                            if (cell?.data) {
                              setTooltip({ data: { ...cell.data, ma_name: ma.ma_name }, x: e.clientX, y: e.clientY });
                            }
                          }}
                          onMouseLeave={() => setTooltip(null)}
                        >
                          {cell && cell.type !== 'abwesenheit' && (
                            <span className={`inline-block w-2 h-2 rounded-full ${CELL_DOT[cell.type] || ''}`} />
                          )}
                          {cell?.type === 'abwesenheit' && cell.data?.von_zeit ? (
                            <span className="text-[9px] font-bold leading-none" style={{ color: cell.color || '#6366F1' }}>h</span>
                          ) : cell?.type === 'abwesenheit' && (
                            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: cell.color || '#6366F1' }} />
                          )}
                          {isFeiertag && !cell && (
                            <span className="text-[9px] text-[var(--text-muted)]">F</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {mitarbeiterList.length === 0 && (
                  <tr><td colSpan={daysInMonth + 1} className="text-center py-8 text-sm text-[var(--text-muted)]">
                    {(searchQuery || onlyWithAbsence || filterAbts.length > 0)
                      ? t('abwesenheiten.kalender.no_results_filter')
                      : (t('common.noData') || 'Keine Daten')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && <CellTooltip data={tooltip.data} x={tooltip.x} y={tooltip.y} t={t} lang={language} />}

      {/* Antrag Modal */}
      <AntragModal
        open={antragModal.open}
        onClose={() => setAntragModal({ open: false, maId: null, date: null })}
        onSuccess={load}
        maId={antragModal.maId}
        startDate={antragModal.date}
        mitarbeiter={konten.map(k => ({ id: k.mitarbeiter_id, name: k.ma_name, gleitzeit_aktiv: k.gleitzeit_aktiv }))}
        abwesenheitsarten={abwesenheitsarten.filter(a => a.aktiv)}
        t={t}
        lang={language}
      />

      {/* Krankenstand Modal */}
      <KrankenstandModal
        open={ksModal.open}
        onClose={() => setKsModal({ open: false, maId: null })}
        onSuccess={load}
        maId={ksModal.maId}
        mitarbeiter={konten.map(k => ({ id: k.mitarbeiter_id, name: k.ma_name }))}
        t={t}
      />

      {/* Tagesansicht-Modal (#1227) */}
      <DayDetailModal
        open={dayDetail.open}
        datum={dayDetail.datum}
        onClose={() => setDayDetail({ open: false, datum: null })}
        t={t}
        lang={language}
      />
    </div>
  );
}
